/**
 * @classdesc Model to represent provider settings.
 * @class
 * @hideconstructor
 */
export class Settings {
  constructor(settingsJson) {
    /** Phone number specified for contacting provider. */
    this.dispatchPhoneNumber = settingsJson.dispatch_phone_number;
    this.stripePublishableKey = settingsJson.stripe_publishable_key;
    this.lockButtonDistanceMeters = settingsJson.lock_button_display_distance;
    /** Company name */
    this.companyName = settingsJson.service_name;
    /** Policy for accepting passwords.
     * @type PasswordPolicy
     */
    this.passswordPolicy = new PasswordPolicy(settingsJson.password_policy);
    /** Boolean to indicate if Jumio is enabled for identity verification. */
    this.jumioEnabled = settingsJson.jumio_enabled;
    /** Boolean to indicate if referrals should be shown. */
    this.enableReferrals = settingsJson.enable_referrals;
    /** Boolean to indicate if rental can be booked instantly, that is starting a rental will unlock the vehicle when successful. */
    this.instantRentalEnabled = settingsJson.instant_unlock_enabled;
    /** Boolean to indicate if license confirmation screen should be shown. */
    this.skipRegistrationLicenseConfirmationScreen =
      settingsJson.jumio_skip_manual_license_correction;
    /** Fully qualified URL to privacy page of provider. */
    this.privacyPageUrl = settingsJson.about_privacy_url;
    /** Fully qualified URL to parking FAQ page of provider. */
    this.parkingFAQPageUrl = settingsJson.about_parking_url;
    /** Fully qualified URL to terms and conditions page of provider. */
    this.agreementPageUrl = settingsJson.about_terms_url;
    /** Fully qualified URL for the customer support page. */
    this.aboutCustomerSupportUrl = settingsJson.about_customer_support_url;
    /** Fully qualified URL to about page of provider. */
    this.aboutPageUrl = settingsJson.about_how_it_works_url;
    /** Fully qualified URL to FAQ page of provider. */
    this.faqPageUrl = settingsJson.about_faq_url;
    this.scheduledRentalRequestsMasterEnabled =
      settingsJson.scheduled_rental_requests_master_enabled;
    this.units = settingsJson.units;
    this.stateCodesFormat = settingsJson.state_codes_csv;
    /** Country code to be used. */
    this.countryCode = settingsJson.country_code;
    /** Currency code to be used. */
    this.currencyCode = settingsJson.currency_code;
    this.currencyPrefix = settingsJson.currency_prefix;
    this.currencySuffix = settingsJson.currency_suffix;
    /** Currency symbol. */
    this.currencySymbol = settingsJson.currency_symbol;
    /** Boolean to indicate if phone number can be edited. */
    this.phoneNumberEditingEnabled =
      settingsJson.customer_editing_phone_number_enabled;
    /** Boolean to indicate of data pipeline should be used for analytics. */
    this.sendMobileEventsToDataPipeline =
      settingsJson.send_mobile_events_to_data_pipeline;
    /** Payment provider. */
    this.paymentProvider = settingsJson.payment_provider;
    /** Identifier of provider. */
    this.tenant = settingsJson.tenant_id;
    this.vehicleTypeDetailsEnabled = settingsJson.vehicle_type_details_enabled;
    this.geoFencingRegistrationEnabled =
      settingsJson.geofencing_registration_enabled;
    /** Fully qualified URL of page to show when user is outside geofence region. */
    this.outsideOfGeofenceRegistrationUrl =
      settingsJson.outside_of_geofence_registration_url;
    /** Boolean to indicate if multiple payment cards are allowed. */
    this.supportForMultiplePaymentCards =
      settingsJson.support_for_multiple_payment_cards;
    this.isCreditPurchaseEnabled = settingsJson.enable_credit_purchase;
    this.creditPurchaseUrl = settingsJson.credit_purchase_url;
    /** Boolean to indicate if identity verification is required. */
    this.isIdentityVerificationRequired =
      settingsJson.identity_verification_required;
    /** Identity verification to be used. */
    this.identityVerificationSystemString =
      settingsJson.identity_verification_system;
    this.isVehicleMarkerTypeEnabled = settingsJson.enable_vehicle_marker_types;
    this.threeDsVerificationAmount = settingsJson.three_ds_verification_amount;
    this.isAccessApiRequired = settingsJson.is_access_api_required;
    /** Boolean to indicate if feedback should be taken about user location on parking. */
    this.sendUserLocationFeedbackOnParkEnabled =
      settingsJson.send_user_location_feedback_on_park;
    this.isScheduledServiceRegionsEnabled =
      settingsJson.scheduled_service_regions_enabled;
    this.collectExternalMembershipNumber =
      settingsJson.collect_external_membership_number;
    /** Boolean to indicate if external memebership number is required. */
    this.requireExternalMembershipNumber =
      settingsJson.require_external_membership_number;
    /** Boolean to indicate if bluetooth should be enabled while using apps. */
    this.makeBleMandatoryForCarsharingCustomerApps =
      settingsJson.make_ble_mandatory_for_carsharing_consumer_apps;
    /** Boolean to indicate if mfa and mfa for staff is enabled or not. */
    this.mfaEnabled = settingsJson.mfa_enabled;
    this.mfaEnabledForStaff = settingsJson.mfa_enabled_for_staff;
    this.isFacilitiesEnabled = settingsJson.enable_facilities;
    /** Fully qualified URL of about page of provider. */
    this.aboutPartnersUrl = settingsJson.about_partners_url;
    /** Fully qualified URL of rates page of provider. */
    this.aboutRatesUrl = settingsJson.about_rates_url;
    this.isRfidOrderingEnabled = settingsJson.enable_rfid_card_ordering;
    /** Boolean to indicate if user agreement is mandatory during sign up. */
    this.isUserAgreementEnabled = settingsJson.user_agreements_enabled;
    this.customerVerificationEnabled = settingsJson.rider_verification_enabled;
    this.customerVerificationModeString = settingsJson.rider_verification_mode;
    this.webUserRegistrationUrl = settingsJson.web_user_registration_url;
    /** Boolean to indicate if registration should be skipped. */
    this.skipAppRegistrationScreen = settingsJson.skip_app_registration_screen;
    /** Boolean to indicate if mailing address is mandatory. */
    this.skipMailingAddress = settingsJson.skip_mailing_address;
    this.twoFactorAuthEnabled = settingsJson.mfa_enabled;
    /** Boolean to indicate if business mode should be provided. */
    this.businessToBusinessEnabled = settingsJson.business_to_business_flow;
    /** Boolean to indicate if insurance add ons is enabled. */
    this.insuranceAddonsEnabled = settingsJson.enable_addons;
    /** Indicates the password reset notification mode. Possible values: sms, email */
    this.passwordResetNotificationType =
      settingsJson.password_reset_notification_type;
    /** Provides the android app link */
    this.androidAppLink = settingsJson.appdownloads_android_app_download_link;
    /** Provides the IOS app link */
    this.iosAppLink = settingsJson.appdownloads_ios_app_download_link;
    this.customerName = settingsJson.customer_name;
    this.serviceLogoUrl = settingsJson.service_logo_url;
    this.use24hrClock = settingsJson.use_24hr_clock_in_ops_center;
    /** Boolean to indicate if web-app is enabled */
    this.enableWebAppRegistrationFlow =
      settingsJson.enable_web_app_registration_flow;
    /** Boolean to indicate if recaptcha is enabled on registration page */
    this.enableCaptcha = settingsJson.enable_captcha;
    /** Phrase project Id for web-app-subscription */
    this.phraseSubscriptionPortalProjectId =
      settingsJson.subscriptions_phrase_project_id;
    /** Phrase project Id for customer-portal */
    this.phraseCustomerPortalProjectId =
      settingsJson.phrase_customer_portal_project_id;
    /** Default locale for phrase integration in customer-portal */
    this.defaultLocale = settingsJson.default_locale;
    /** Boolean to indicate whether the payments enabled or not */
    this.paymentsEnabled = settingsJson.payments_enabled;
    this.businessInviteCodeMandatory =
      settingsJson.business_invite_code_mandatory;
    /** Boolean to indicate whether we can show external driver id */
    this.enableExternalDriverId = settingsJson.enable_external_driver_id;
  }
}

/**
 * @classdesc Password policy settings.
 * @class
 * @hideconstructor
 */
class PasswordPolicy {
  constructor(passswordPolicy) {
    if (passswordPolicy) {
      /** Identifier of password policy. */
      this.id = passswordPolicy.id;
      /** Number of previous passwords to differ from. */
      this.numberOfPreviousPasswordsToDifferFrom =
        passswordPolicy.last_x_passwords;
      /** Maximum number of characters allowed in password. */
      this.maximumNumberOfCharacters = passswordPolicy.max_chars;
      /** Minimum number of characters required in password. */
      this.minimumNumberOfCharacters = passswordPolicy.min_chars;
      /** Boolean to indicate if lower case characters are required. */
      this.isLowercaseCharacterRequired = passswordPolicy.require_lowercase;
      /** Boolean to indicate if numbers are required. */
      this.isNumericCharacterRequired = passswordPolicy.require_number;
      /** Boolean to indicate if special characters are required. */
      this.isSpecialCharacterRequired = passswordPolicy.require_special;
      /** Boolean to indicate if upper case character is required. */
      this.isUppercaseCharacterRequired = passswordPolicy.require_uppercase;
    }
  }
}
